import { sortGrades } from '@/browse/grades'
import { wikiToPlainText } from '@/util/tokenize_wiki_text'
import { QUARTER_NUMBERS, type ObjectiveLevel } from '../objectives/objective_levels'
import type { GenerationTargetType } from './generation_stream'

export const ANNUAL_TARGET_LABEL = 'Annual Target'

/** What the generate modal is about, and the type/level pair the generate request needs. */
export interface GenerationTarget {
  targetType: GenerationTargetType
  targetLevel: ObjectiveLevel | null
  targetLabel: string
  targetText: string
}

/** "Quarter 2 Objective", parallel with "Annual Target"; an unexpected level reads "Objective". */
export function objectiveTargetLabel(level: string): string {
  return Object.hasOwn(QUARTER_NUMBERS, level) ? `Quarter ${QUARTER_NUMBERS[level as ObjectiveLevel]} Objective` : 'Objective'
}

/**
 * The plain text a target is shown and generated from. A cleared edit ('') stays cleared;
 * only a missing edit falls back to the stored text.
 */
export function generationTargetText(editedText: string | null | undefined, fallbackText = ''): string {
  const source = editedText ?? fallbackText
  return source ? wikiToPlainText(source) : ''
}

/** The route's single `grade`: one grade, or a range across a multi-grade page. */
export function generationGrade(grades: string[] | null | undefined): string {
  if (!grades?.length) return ''
  const sorted = sortGrades(grades)
  const first = sorted[0]
  const last = sorted[sorted.length - 1]
  return first === last ? first : `${first}-${last}`
}

export function annualGenerationTarget(annualText: string | null | undefined): GenerationTarget {
  return {
    targetType: 'goal',
    targetLevel: null,
    targetLabel: ANNUAL_TARGET_LABEL,
    targetText: generationTargetText(annualText)
  }
}

export function objectiveGenerationTarget(level: ObjectiveLevel, editedText: string | null | undefined, savedText = ''): GenerationTarget {
  return {
    targetType: 'objective',
    targetLevel: level,
    targetLabel: objectiveTargetLabel(level),
    targetText: generationTargetText(editedText, savedText)
  }
}
